import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { getVisibleNavItems } from '@/shared/layout/nav-items';

interface DashboardSidebarProps {
  isSuperAdmin: boolean;
  mobileOpen: boolean;
  onClose: () => void;
}

const linkBase =
  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-muted-foreground hover:bg-muted/60 hover:text-foreground';
const linkActive = 'bg-primary/10 text-primary';

export function DashboardSidebar({ isSuperAdmin, mobileOpen, onClose }: DashboardSidebarProps) {
  const { t } = useTranslation();
  const items = getVisibleNavItems(isSuperAdmin);

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-label={t('nav.closeMenu')}
          onClick={onClose}
        />
      ) : null}

      <aside
        className={cn(
          'fixed top-0 start-0 z-50 flex h-full w-64 flex-col border-e border-border bg-card shadow-lg transition-transform duration-200',
          'lg:static lg:z-auto lg:h-auto lg:translate-x-0 lg:shadow-none',
          mobileOpen
            ? 'max-lg:translate-x-0'
            : 'max-lg:-translate-x-full max-lg:rtl:translate-x-full',
        )}
        aria-label={t('nav.sidebar')}
      >
        <div className="flex h-14 items-center gap-3 border-b border-border px-4">
          <div
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ background: 'var(--gradient-primary)' }}
          >
            <span className="text-sm font-bold text-white">SA</span>
          </div>
          <span className="font-semibold text-foreground truncate">{t('app.name')}</span>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                {...(item.exact ? { activeOptions: { exact: true as const } } : {})}
                className={linkBase}
                activeProps={{ className: cn(linkBase, linkActive) }}
                onClick={onClose}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{t(item.labelKey)}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
